import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AssessmentsAdmin from '../components/AssessmentsAdmin';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  ExternalLink, 
  Home, 
  Users, 
  LogOut,
  Loader2,
  CheckCircle,
  Building2,
  Upload,
  FileSpreadsheet,
  Download,
  Image as ImageIcon,
  BookOpen,
  TrendingUp,
  Calendar,
  Phone,
  Mail,
  Zap,
  Lock,
  Unlock,
  Eye,
  XCircle,
  AlertTriangle,
  Clock,
  Check,
  X,
  FileText,
  Key,
  RotateCcw,
  Briefcase,
  Send,
  Trophy,
  Medal,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ClipboardList
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, isAdmin, logout, loading: authLoading } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [deletingCandidate, setDeletingCandidate] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [formLoading, setFormLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  
  // Learn Communication state
  const [activeTab, setActiveTab] = useState('candidates'); // 'candidates', 'learners', 'recruiters', 'placement', or 'coldcall'
  const [learners, setLearners] = useState([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [learnerStats, setLearnerStats] = useState(null);
  
  // Recruiters state
  const [recruiters, setRecruiters] = useState([]);
  const [recruitersLoading, setRecruitersLoading] = useState(false);
  const [recruiterStats, setRecruiterStats] = useState(null);
  
  // Placement AI state
  const [placementStudents, setPlacementStudents] = useState([]);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [placementStats, setPlacementStats] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [showCertUploadDialog, setShowCertUploadDialog] = useState(false);
  const [certUploadStudent, setCertUploadStudent] = useState(null);
  
  // Cold Call Lab state
  const [coldcallUsers, setColdcallUsers] = useState([]);
  const [coldcallLoading, setColdcallLoading] = useState(false);
  const [coldcallStats, setColdcallStats] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewStep, setReviewStep] = useState(null);
  const [reviewComments, setReviewComments] = useState('');
  
  // Forgot Password Requests state
  const [placementForgotRequests, setPlacementForgotRequests] = useState([]);
  const [coldcallForgotRequests, setColdcallForgotRequests] = useState([]);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetModule, setResetModule] = useState(''); // 'placement' or 'coldcall'
  
  // Attendance Report state
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  
  // Leaderboard state
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  
  // Admin Notifications state
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // Outreach Review state
  const [outreachReviewData, setOutreachReviewData] = useState(null);
  const [outreachReviewStudent, setOutreachReviewStudent] = useState(null);
  const [outreachReviewLoading, setOutreachReviewLoading] = useState(false);
  
  // Phase 2 Outreach Tracking state
  const [phase2TrackingData, setPhase2TrackingData] = useState(null);
  const [phase2TrackingLoading, setPhase2TrackingLoading] = useState(false);
  const [phase2TrackingFilter, setPhase2TrackingFilter] = useState('');
  
  // Internship Metrics state
  const [internshipMetrics, setInternshipMetrics] = useState([]);
  const [expandedMetricsStudents, setExpandedMetricsStudents] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [selectedMetricsStudent, setSelectedMetricsStudent] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    photo_url: '',
    portfolio_link: '',
    strength: '',
    expected_ctc: '',
    is_placed: false,
    placed_company_name: '',
    placed_company_logo: ''
  });

  // Convert photo URLs to displayable image URLs
  const getDirectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=100&h=100&fit=crop';
    
    // Handle relative API paths (e.g., /api/images/uuid) - persistent across deployments
    if (url.startsWith('/api/')) {
      return `${BACKEND_URL}${url}`;
    }
    
    // Handle old full URLs that contain /api/images/ (from previous deployments)
    if (url.includes('/api/images/')) {
      const imageId = url.split('/api/images/')[1];
      return `${BACKEND_URL}/api/images/${imageId}`;
    }
    
    // Extract Google Drive file ID from various URL formats
    let fileId = null;
    
    // Format: drive.google.com/open?id=FILE_ID
    if (url.includes('drive.google.com/open?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    // Format: drive.google.com/file/d/FILE_ID/view
    else if (url.includes('drive.google.com/file/d/')) {
      fileId = url.split('/d/')[1]?.split('/')[0];
    }
    // Format: drive.google.com/uc?id=FILE_ID
    else if (url.includes('drive.google.com/uc?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    
    if (fileId) {
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
    
    return url;
  };

  useEffect(() => {
    // Wait for auth to load before checking admin status
    if (authLoading) return;
    
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchCandidates();
  }, [isAdmin, authLoading, navigate]);

  const fetchCandidates = async () => {
    try {
      const response = await axios.get(`${API}/candidates/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(response.data);
    } catch (error) {
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchLearners = async () => {
    setLearnersLoading(true);
    try {
      const response = await axios.get(`${API}/learn/admin/learners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearners(response.data.learners || []);
      setLearnerStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch learners:', error);
    } finally {
      setLearnersLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    setRecruitersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/recruiters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiters(response.data.users || []);
      setRecruiterStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch recruiters:', error);
      toast.error('Failed to fetch recruiters');
    } finally {
      setRecruitersLoading(false);
    }
  };

  const fetchPlacementStudents = async () => {
    setPlacementLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlacementStudents(response.data.students || []);
      setPlacementStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch placement students:', error);
      toast.error('Failed to fetch placement students');
    } finally {
      setPlacementLoading(false);
    }
  };

  const fetchPhase2Tracking = async () => {
    setPhase2TrackingLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/phase2-outreach-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhase2TrackingData(response.data.students || []);
    } catch (error) {
      console.error('Failed to fetch Phase 2 tracking:', error);
    } finally {
      setPhase2TrackingLoading(false);
    }
  };


  const fetchPlacementForgotRequests = async () => {
    try {
      const response = await axios.get(`${API}/placement/admin/forgot-password-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlacementForgotRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch forgot password requests:', error);
    }
  };

  const fetchColdcallForgotRequests = async () => {
    try {
      const response = await axios.get(`${API}/coldcall/admin/forgot-password-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColdcallForgotRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch forgot password requests:', error);
    }
  };

  const resetUserPassword = async () => {
    if (!resetPasswordEmail || !newPassword) {
      toast.error('Please enter email and new password');
      return;
    }
    try {
      const endpoint = resetModule === 'placement' 
        ? `${API}/placement/admin/reset-password/${resetPasswordEmail}`
        : `${API}/coldcall/admin/reset-password/${resetPasswordEmail}`;
      
      await axios.post(endpoint, { new_password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password reset successful for ${resetPasswordEmail}. Please inform the user of their new password.`);
      setShowResetModal(false);
      setResetPasswordEmail('');
      setNewPassword('');
      // Refresh the requests
      if (resetModule === 'placement') {
        fetchPlacementForgotRequests();
      } else {
        fetchColdcallForgotRequests();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const dismissForgotRequest = async (requestId, module) => {
    try {
      const endpoint = module === 'placement'
        ? `${API}/placement/admin/forgot-password-request/${requestId}`
        : `${API}/coldcall/admin/forgot-password-request/${requestId}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request dismissed');
      if (module === 'placement') {
        fetchPlacementForgotRequests();
      } else {
        fetchColdcallForgotRequests();
      }
    } catch (error) {
      toast.error('Failed to dismiss request');
    }
  };

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const fetchAttendanceReport = async () => {
    setAttendanceLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/attendance-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceReport(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance report:', error);
      toast.error('Failed to fetch attendance report');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Fetch admin notifications (Step 4 corrections, WhatsApp requests, etc.)
  const fetchAdminNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Fetch internship metrics
  const fetchInternshipMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await axios.get(`${API}/placement/admin/internship-metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInternshipMetrics(response.data.students || []);
    } catch (error) {
      console.error('Failed to fetch internship metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Approve WhatsApp group request
  const approveWhatsApp = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/approve-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp group access approved!');
      fetchAdminNotifications();
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  // Approve WhatsApp switch request
  const approveWhatsAppSwitch = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/approve-whatsapp-switch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp switch approved!');
      fetchAdminNotifications();
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/placement/admin/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAdminNotifications();
    } catch (error) {
      console.error('Failed to mark notification:', error);
    }
  };

  // Mark internship as complete (unlocks metrics submission)
  const markInternshipComplete = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/mark-internship-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Internship marked complete! Student can now submit metrics.');
      setOutreachReviewData(null);
      setOutreachReviewStudent(null);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark complete');
    }
  };

  const fetchOutreachReview = async (studentId, studentName) => {
    setOutreachReviewLoading(true);
    setOutreachReviewStudent({ id: studentId, name: studentName });
    try {
      const response = await axios.get(`${API}/placement/admin/student/${studentId}/outreach-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutreachReviewData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load outreach data');
      setOutreachReviewStudent(null);
    } finally {
      setOutreachReviewLoading(false);
    }
  };

  // Phase 2 admin handlers
  const phase2MarkWhatsappSent = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/phase2-whatsapp-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp link marked as sent! Step 2 unlocked.');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const [phase2DocLink, setPhase2DocLink] = useState('');
  const [phase2DocStudentId, setPhase2DocStudentId] = useState(null);

  const phase2SendJobProfileLink = async (studentId) => {
    if (!phase2DocLink.trim()) {
      toast.error('Please enter the document link');
      return;
    }
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/phase2-job-profile-link`, 
        { doc_link: phase2DocLink },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Job Profile document link sent!');
      setPhase2DocLink('');
      setPhase2DocStudentId(null);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  // Download internship metrics as CSV
  const downloadMetricsCSV = (student) => {
    if (!student) return;
    const m = student.internship_metrics || {};
    const headers = ['Name', 'Email', 'Companies Outreached', 'Total Leads', 'LinkedIn Outreach', 'Email Outreach', 'Call Outreach', 'Meetings Set', 'Responses', 'Positive Replies', 'Hours Spent', 'Market Focused', 'Tools Used'];
    const row = [student.name, student.email, m.companies_outreached || '', m.total_leads_generated || '', m.total_linkedin_outreach || '', m.total_email_outreach || '', m.total_call_outreach || '', m.meetings_set || '', m.responses_received || '', m.positive_replies || '', m.total_hours_spent || '', (m.market_focused || '').replace(/,/g, ';'), (m.tools_used || '').replace(/,/g, ';')];
    const csvContent = [headers.join(','), row.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${student.name.replace(/\s+/g, '-').toLowerCase()}_internship-metrics.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Metrics downloaded for ${student.name}!`);
  };


  const approveStudent = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student approved!');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve student');
    }
  };

  const rejectStudent = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student rejected');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject student');
    }
  };

  const lockStudent = async (studentId) => {
    const reason = prompt('Enter lock reason:');
    if (!reason) return;
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/lock?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student locked');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock student');
    }
  };

  const unlockStudent = async (studentId) => {
    if (!confirm('This will reset the student to Step 1. Continue?')) return;
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student unlocked and reset to Step 1');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock student');
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE "${studentName}"? This action cannot be undone.`)) return;
    if (!confirm(`This is your final confirmation. Delete "${studentName}"?`)) return;
    try {
      await axios.delete(`${API}/placement/admin/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Student "${studentName}" has been deleted`);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const resetDecisionPath = async (studentId, studentName, newPath) => {
    const pathLabel = newPath === null ? 'reset (choose again)' : 
                      newPath === 'internship' ? 'Internship + Outreach' : 'Direct Outreach';
    if (!confirm(`Change ${studentName}'s path to ${pathLabel}? This will clear their current progress on that path.`)) return;
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/reset-decision`, 
        { new_path: newPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Path changed to ${pathLabel} for ${studentName}`);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change path');
    }
  };

  const reviewStudentStep = async (studentId, stepNum, action) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/review-step/${stepNum}`, 
        { action, comments: reviewComments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Step ${stepNum} ${action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewDialogOpen(false);
      setReviewComments('');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
  };

  const completeSession = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/complete-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session marked as completed');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    }
  };

  const verifyCorrections = async (studentId) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/verify-corrections`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Corrections verified');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify corrections');
    }
  };

  const reviewInternshipStep = async (studentId, stepNum, action) => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/review-internship-step/${stepNum}`, 
        { action, comments: reviewComments || 'Approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Internship step ${stepNum} ${action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewDialogOpen(false);
      setReviewComments('');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
  };

  // Company List Upload State
  const [companyListDialog, setCompanyListDialog] = useState({ open: false, student: null });
  const [companyListForm, setCompanyListForm] = useState({ url: '', notes: '' });

  const uploadCompanyList = async () => {
    if (!companyListDialog.student || !companyListForm.url) return;
    try {
      await axios.post(
        `${API}/placement/admin/student/${companyListDialog.student.id}/upload-company-list`,
        { company_list_url: companyListForm.url, admin_notes: companyListForm.notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Company list uploaded for ${companyListDialog.student.name}`);
      setCompanyListDialog({ open: false, student: null });
      setCompanyListForm({ url: '', notes: '' });
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const generateCertificate = async (studentId) => {
    if (!certificateFile) {
      toast.error('Please select a PDF file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', certificateFile);
    
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/upload-certificate`, 
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success('Certificate uploaded! Student can now download it.');
      setCertificateFile(null);
      setShowCertUploadDialog(false);
      setCertUploadStudent(null);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload certificate');
    }
  };
  
  const openCertUploadDialog = (student) => {
    setCertUploadStudent(student);
    setCertificateFile(null);
    setShowCertUploadDialog(true);
  };

  const reviewMegamindEligibility = async (studentId, action, rejectionReason = '') => {
    try {
      await axios.post(`${API}/placement/admin/student/${studentId}/review-eligibility`, 
        { action, comments: action === 'approve' ? 'Approved' : rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Megamind eligibility ${action === 'approve' ? 'approved' : 'rejected'}`);
      setShowMegamindDocsDialog(false);
      setMegamindDocsStudent(null);
      setRejectionReason('');
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
  };

  const [showMegamindDocsDialog, setShowMegamindDocsDialog] = useState(false);
  const [megamindDocsStudent, setMegamindDocsStudent] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const openMegamindDocsDialog = (student) => {
    setMegamindDocsStudent(student);
    setRejectionReason('');
    setShowMegamindDocsDialog(true);
  };

  // Complete Mock Call for Interview
  const completeMockCall = async (studentId, interviewId, companyName) => {
    try {
      await axios.post(
        `${API}/placement/admin/interviews/${studentId}/${interviewId}/complete-mock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Mock call completed for ${companyName}`);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete mock call');
    }
  };

  const fetchColdcallUsers = async () => {
    setColdcallLoading(true);
    try {
      const response = await axios.get(`${API}/coldcall/admin/users`);
      setColdcallUsers(response.data.users || []);
      setColdcallStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch cold call users:', error);
    } finally {
      setColdcallLoading(false);
    }
  };

  // Review Offer Letter
  const reviewOfferLetter = async (studentId, action, studentName, companyName) => {
    try {
      await axios.post(
        `${API}/placement/admin/student/${studentId}/review-offer-letter?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Offer letter ${action}d for ${studentName} (${companyName})`);
      fetchPlacementStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review offer letter');
    }
  };

  const approveColdcallUser = async (userId) => {
    try {
      await axios.post(`${API}/coldcall/admin/user/${userId}/approve`);
      toast.success('User approved!');
      fetchColdcallUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const rejectColdcallUser = async (userId) => {
    try {
      await axios.post(`${API}/coldcall/admin/user/${userId}/reject`);
      toast.success('User rejected');
      fetchColdcallUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  // Fetch learners when tab changes
  useEffect(() => {
    if (activeTab === 'learners' && learners.length === 0) {
      fetchLearners();
    }
    if (activeTab === 'recruiters' && recruiters.length === 0) {
      fetchRecruiters();
    }
    if (activeTab === 'placement') {
      if (placementStudents.length === 0) {
        fetchPlacementStudents();
        fetchAttendanceReport();
      }
      // Always fetch forgot password requests and notifications when tab is active
      fetchPlacementForgotRequests();
      fetchAdminNotifications();
      fetchInternshipMetrics();
      fetchPhase2Tracking();
    }
    if (activeTab === 'coldcall') {
      if (coldcallUsers.length === 0) {
        fetchColdcallUsers();
      }
      // Always fetch forgot password requests when tab is active
      fetchColdcallForgotRequests();
    }
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openAddDialog = () => {
    setEditingCandidate(null);
    setFormData({
      name: '',
      photo_url: '',
      portfolio_link: '',
      strength: '',
      expected_ctc: '',
      is_placed: false,
      placed_company_name: '',
      placed_company_logo: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      name: candidate.name,
      photo_url: candidate.photo_url,
      portfolio_link: candidate.portfolio_link,
      strength: candidate.strength,
      expected_ctc: candidate.expected_ctc || '',
      is_placed: candidate.is_placed,
      placed_company_name: candidate.placed_company_name || '',
      placed_company_logo: candidate.placed_company_logo || ''
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (candidate) => {
    setDeletingCandidate(candidate);
    setDeleteDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, is_placed: checked });
  };

  // Excel Upload Handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/candidates/upload-excel`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (response.data.success) {
        toast.success(
          `Upload successful! Added: ${response.data.candidates_added}, Updated: ${response.data.candidates_updated}`
        );
        setUploadDialogOpen(false);
        fetchCandidates();
      } else {
        toast.error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Photo Upload Handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 5MB');
      return;
    }

    setPhotoUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/upload-photo`,
        uploadData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (response.data.success) {
        // Store RELATIVE path (persists across deployments)
        const photoUrl = response.data.photo_url;
        setFormData(prev => ({ ...prev, photo_url: photoUrl }));
        toast.success('Photo uploaded successfully!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Photo upload failed';
      toast.error(message);
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingCandidate) {
        await axios.put(
          `${API}/admin/candidates/${editingCandidate.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Candidate updated successfully');
      } else {
        await axios.post(
          `${API}/admin/candidates`,
          {
            name: formData.name,
            photo_url: formData.photo_url,
            portfolio_link: formData.portfolio_link,
            strength: formData.strength,
            expected_ctc: formData.expected_ctc
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Candidate added successfully');
      }
      setDialogOpen(false);
      fetchCandidates();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCandidate) return;
    setFormLoading(true);

    try {
      await axios.delete(
        `${API}/admin/candidates/${deletingCandidate.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Candidate deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingCandidate(null);
      fetchCandidates();
    } catch (error) {
      toast.error('Failed to delete candidate');
    } finally {
      setFormLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-hfs-navy" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-hfs-navy text-white p-6">
        <div className="flex items-center gap-3 mb-10">
          <img src={HFS_LOGO} alt="HFS" className="h-10 w-10" />
          <div>
            <h1 className="font-heading font-bold text-lg">HFS Admin</h1>
            <p className="text-xs text-slate-300">Management Panel</p>
          </div>
        </div>

        <nav className="space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/10 transition-colors"
          >
            <Home className="h-5 w-5" />
            Back to Website
          </Link>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'candidates' 
                ? 'bg-white/10 text-white' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
          >
            <Users className="h-5 w-5" />
            Candidates
          </button>
          <button
            onClick={() => setActiveTab('learners')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'learners' 
                ? 'bg-white/10 text-white' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
          >
            <BookOpen className="h-5 w-5" />
            Learn Communication
          </button>
          <button
            onClick={() => setActiveTab('recruiters')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'recruiters' 
                ? 'bg-white/10 text-white' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
            data-testid="recruiters-tab"
          >
            <Building2 className="h-5 w-5" />
            Recruiters
          </button>
          <button
            onClick={() => setActiveTab('placement')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'placement' 
                ? 'bg-amber-500/20 text-amber-400' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
            data-testid="placement-ai-tab"
          >
            <Zap className="h-5 w-5" />
            Placement AI
          </button>
          <button
            onClick={() => setActiveTab('coldcall')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'coldcall' 
                ? 'bg-cyan-500/20 text-cyan-400' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
            data-testid="coldcall-tab"
          >
            <Phone className="h-5 w-5" />
            Cold Call Lab
          </button>
          <button
            onClick={() => setActiveTab('assessments')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'assessments' 
                ? 'bg-indigo-500/20 text-indigo-400' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
            data-testid="assessments-tab"
          >
            <ClipboardList className="h-5 w-5" />
            Assessments
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="text-sm text-slate-300 mb-3">
            Logged in as:<br />
            <span className="text-white font-medium">{user?.email}</span>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="admin-logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          {activeTab === 'assessments' && (
            <AssessmentsAdmin token={token} />
          )}
          {activeTab === 'candidates' && (
            <>
              {/* Candidates Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900">Candidates</h1>
                  <p className="text-slate-500 mt-1">Manage your trained candidates</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setUploadDialogOpen(true)}
                    variant="outline"
                    className="border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5"
                    data-testid="upload-excel-btn"
                  >
                <Upload className="mr-2 h-4 w-4" />
                Upload Excel
              </Button>
              <Button
                onClick={openAddDialog}
                className="bg-hfs-navy hover:bg-hfs-navy-dark text-white"
                data-testid="add-candidate-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Candidate
              </Button>
            </div>
          </div>

          {/* Candidates Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">All Candidates</CardTitle>
              <Button
                onClick={fetchCandidates}
                variant="outline"
                size="sm"
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                      <path d="M16 21h5v-5" />
                    </svg>
                    Refresh
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-hfs-navy" />
                </div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No candidates yet. Add your first candidate!</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Photo</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Strength</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Portfolio</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {candidates.map((candidate) => (
                      <TableRow key={candidate.id} data-testid={`candidate-row-${candidate.id}`}>
                        <TableCell>
                          <img
                            src={getDirectImageUrl(candidate.photo_url)}
                            alt={candidate.name}
                            className="h-12 w-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=100&h=100&fit=crop';
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{candidate.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-slate-100">
                            {candidate.strength}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {candidate.is_placed ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Placed
                              </Badge>
                              {candidate.placed_company_name && (
                                <span className="text-sm text-slate-500">
                                  at {candidate.placed_company_name}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              Available
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <a
                            href={candidate.portfolio_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-hfs-navy hover:underline inline-flex items-center gap-1"
                          >
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`candidate-actions-${candidate.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(candidate)} data-testid={`edit-candidate-${candidate.id}`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openDeleteDialog(candidate)}
                                className="text-red-600"
                                data-testid={`delete-candidate-${candidate.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
            </>
          )}

          {activeTab === 'learners' && (
            /* Learn Communication Learners Section */
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900">Learn Communication</h1>
                  <p className="text-slate-500 mt-1">View registered learners and their progress</p>
                </div>
                <Button
                  onClick={fetchLearners}
                  variant="outline"
                  className="border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5"
                >
                  Refresh
                </Button>
              </div>

              {/* Stats Cards */}
              {learnerStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{learnerStats.total_learners}</p>
                          <p className="text-xs text-slate-500">Total Learners</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{learnerStats.active_learners}</p>
                          <p className="text-xs text-slate-500">Active Learners</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{learnerStats.completed_learners}</p>
                          <p className="text-xs text-slate-500">Completed</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-orange-100 p-2 rounded-lg">
                          <Calendar className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{learnerStats.avg_progress}%</p>
                          <p className="text-xs text-slate-500">Avg Progress</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Learners Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Registered Learners</CardTitle>
                </CardHeader>
                <CardContent>
                  {learnersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-hfs-navy" />
                    </div>
                  ) : learners.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <BookOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No learners registered yet</p>
                      <p className="text-sm mt-1">Learners who register on Learn Communication will appear here</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Day</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Registered</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {learners.map((learner) => (
                          <TableRow key={learner.id}>
                            <TableCell className="font-medium">{learner.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="h-3 w-3 text-slate-400" />
                                {learner.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Phone className="h-3 w-3 text-slate-400" />
                                {learner.mobile}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {learner.current_role?.replace('_', ' ') || 'Student'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-hfs-navy">Day {learner.current_day}/30</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 rounded-full" 
                                    style={{ width: `${learner.overall_progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-slate-600">{learner.overall_progress}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(learner.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Recruiters Tab Content */}
          {activeTab === 'recruiters' && (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900">Recruiters</h1>
                  <p className="text-slate-500 mt-1">View registered recruiters and companies</p>
                </div>
                <Button
                  onClick={fetchRecruiters}
                  variant="outline"
                  className="border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5"
                  data-testid="refresh-recruiters-btn"
                >
                  Refresh
                </Button>
              </div>

              {/* Recruiter Stats Cards */}
              {recruiterStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.total_users}</p>
                          <p className="text-xs text-slate-500">Total Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Users className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.total_recruiters}</p>
                          <p className="text-xs text-slate-500">Recruiters</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg">
                          <Building2 className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.unique_companies}</p>
                          <p className="text-xs text-slate-500">Companies</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-orange-100 p-2 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.new_last_7_days}</p>
                          <p className="text-xs text-slate-500">Last 7 Days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-teal-100 p-2 rounded-lg">
                          <Calendar className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.new_last_30_days}</p>
                          <p className="text-xs text-slate-500">Last 30 Days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-red-100 p-2 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{recruiterStats.total_admins}</p>
                          <p className="text-xs text-slate-500">Admins</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Recruiters Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Registered Recruiters & HRs</CardTitle>
                </CardHeader>
                <CardContent>
                  {recruitersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-hfs-navy" />
                    </div>
                  ) : recruiters.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No recruiters registered yet</p>
                      <p className="text-sm mt-1">Users who register to view candidates will appear here</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Designation</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Registered</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recruiters.map((recruiter) => (
                          <TableRow key={recruiter.id} data-testid={`recruiter-row-${recruiter.id}`}>
                            <TableCell className="font-medium">{recruiter.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="h-3 w-3 text-slate-400" />
                                {recruiter.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Phone className="h-3 w-3 text-slate-400" />
                                {recruiter.phone || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Building2 className="h-3 w-3 text-slate-400" />
                                <span className="text-sm">{recruiter.company || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{recruiter.designation || '-'}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={recruiter.role === 'admin' ? 'default' : 'outline'}
                                className={recruiter.role === 'admin' ? 'bg-hfs-navy' : ''}
                              >
                                {recruiter.role === 'admin' ? 'Admin' : 'Recruiter'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recruiter.created_at ? new Date(recruiter.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              }) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Placement AI Tab Content */}
          {activeTab === 'placement' && (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
                    <Zap className="h-8 w-8 text-amber-500" />
                    Placement AI
                  </h1>
                  <p className="text-slate-500 mt-1">Manage student workflow and approvals</p>
                </div>
                <Button
                  onClick={fetchPlacementStudents}
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  data-testid="refresh-placement-btn"
                >
                  Refresh
                </Button>
              </div>

              {/* Placement Stats Cards */}
              {placementStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-slate-500 mb-1">Total</p>
                      <p className="text-2xl font-bold text-slate-900">{placementStats.total_students}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-amber-600 mb-1">Pending</p>
                      <p className="text-2xl font-bold text-amber-600">{placementStats.pending_approval}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-green-600 mb-1">Approved</p>
                      <p className="text-2xl font-bold text-green-600">{placementStats.approved}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-red-600 mb-1">Rejected</p>
                      <p className="text-2xl font-bold text-red-600">{placementStats.rejected}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-red-600 mb-1">Locked</p>
                      <p className="text-2xl font-bold text-red-600">{placementStats.locked}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-slate-500 mb-1">Avg Score</p>
                      <p className="text-2xl font-bold text-slate-900">{placementStats.average_score}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-slate-500 mb-1">Referral Ready</p>
                      <p className="text-2xl font-bold text-green-600">
                        {placementStudents.filter(s => s.scores?.total >= 75).length}
                      </p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:bg-amber-50 transition-colors border-amber-200"
                    onClick={() => { fetchLeaderboard(); setShowLeaderboard(true); }}
                  >
                    <CardContent className="pt-4 pb-4">
                      <p className="text-xs text-slate-500 mb-1">View</p>
                      <p className="text-lg font-bold text-amber-600 flex items-center gap-1">
                        <Trophy className="h-5 w-5" />
                        Leaderboard
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Pending Actions Alert Section */}
              {(() => {
                const pendingActions = [];
                
                // Add notifications from admin_notifications collection (Step 4 corrections, etc.)
                adminNotifications.forEach(notification => {
                  if (notification.type === 'corrections_done') {
                    pendingActions.push({
                      type: 'corrections_done',
                      priority: 'high',
                      student: { id: notification.student_id, name: notification.student_name },
                      message: notification.message,
                      action: 'Review & Approve',
                      notificationId: notification.id
                    });
                  }
                });

                placementStudents.forEach(student => {
                  // Check for pending account approvals
                  if (student.status === 'pending') {
                    pendingActions.push({
                      type: 'account_approval',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Account pending approval`,
                      action: 'Approve/Reject account'
                    });
                  }
                  
                  // Check for 1:1 session requests (Step 3 in new workflow)
                  if (student.current_step === 3 && student.submissions?.step_3?.status === 'requested') {
                    pendingActions.push({
                      type: 'session_request',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Requested 1:1 Session`,
                      action: 'Complete Session'
                    });
                  }
                  
                  // Check for pending step approvals (Steps 1, 2)
                  ['step_1', 'step_2'].forEach((stepKey, idx) => {
                    const submission = student.submissions?.[stepKey];
                    if (submission && !submission.admin_review && student.status === 'approved') {
                      pendingActions.push({
                        type: 'step_approval',
                        priority: 'medium',
                        student: student,
                        stepNum: idx + 1,
                        message: `${student.name} - Step ${idx + 1} submission needs review`,
                        action: 'Review & Approve'
                      });
                    }
                  });
                  
                  // Check for corrections confirmation (Step 4 - "All Corrections Done")
                  if (student.submissions?.step_4?.status === 'pending_verification') {
                    pendingActions.push({
                      type: 'corrections_verification',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - All Corrections Done, needs your review`,
                      action: 'Review & Approve'
                    });
                  }
                  
                  // Check for internship step submissions
                  if (student.decision_path === 'internship' && student.internship_data?.submissions) {
                    const internshipStepNames = {
                      1: 'Join WhatsApp Group',
                      2: 'Watch Internship Video',
                      3: 'Submit LinkedIn Profile',
                      4: 'Request for Company List',
                      5: 'Research & Prospect Data',
                      6: 'Submit Internship Metrics'
                    };
                    
                    // Check for WhatsApp group request (Step 1)
                    if (student.internship_data?.whatsapp_status === 'requested') {
                      pendingActions.push({
                        type: 'whatsapp_request',
                        priority: 'high',
                        student: student,
                        message: `${student.name} - Requested to join Internship WhatsApp Group`,
                        action: 'Approve & Send Link'
                      });
                    }
                    
                    // Check for company list request (Step 4 special workflow)
                    const step4 = student.internship_data.submissions['step_4'];
                    if (step4?.status === 'requested') {
                      pendingActions.push({
                        type: 'company_list_upload',
                        priority: 'high',
                        student: student,
                        message: `${student.name} - Requested Company List`,
                        action: 'Upload Company List'
                      });
                    }
                    
                    // Check other steps for pending review (3, 5, 6)
                    [3, 5, 6].forEach((stepNum) => {
                      const stepKey = `step_${stepNum}`;
                      const submission = student.internship_data.submissions[stepKey];
                      if (submission?.status === 'pending_review' && !submission.admin_review) {
                        pendingActions.push({
                          type: 'internship_approval',
                          priority: 'high',
                          student: student,
                          stepNum: stepNum,
                          submittedLink: submission.link || submission.notes || '',
                          submittedNotes: submission.notes || '',
                          message: `${student.name} - Internship: ${internshipStepNames[stepNum]} needs review`,
                          action: 'Review & Approve'
                        });
                      }
                    });
                  }
                  
                  // Check for WhatsApp switch request
                  if (student.megamind_data?.whatsapp_switch_status === 'requested') {
                    pendingActions.push({
                      type: 'whatsapp_switch_request',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Wants to switch to Outreach WhatsApp Group`,
                      action: 'Approve & Send Link'
                    });
                  }
                  
                  // Check for Phase 2 WhatsApp request
                  if (student.megamind_data?.phase2_flow?.whatsapp?.requested && !student.megamind_data?.phase2_flow?.whatsapp?.completed) {
                    pendingActions.push({
                      type: 'phase2_whatsapp_request',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Requested to join Outreach WhatsApp Group (Phase 2)`,
                      action: 'Link Sent'
                    });
                  }
                  
                  // Check for Phase 2 Job Profile request
                  if (student.megamind_data?.phase2_flow?.job_profile?.requested && !student.megamind_data?.phase2_flow?.job_profile?.doc_link) {
                    pendingActions.push({
                      type: 'phase2_job_profile_request',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Requested Job Profile Mandate Document (Phase 2)`,
                      action: 'Send Document Link'
                    });
                  }
                  
                  // Check if internship outreach is complete and needs to be marked complete by admin
                  if (student.decision_path === 'internship' && student.internship_data?.internship_started && !student.internship_data?.internship_completed) {
                    const outreachDays = student.outreach_data?.completed_days?.length || 0;
                    if (outreachDays >= 10) {
                      pendingActions.push({
                        type: 'mark_internship_complete',
                        priority: 'high',
                        student: student,
                        message: `${student.name} - 10-Day Outreach completed. Mark internship complete to unlock metrics.`,
                        action: 'Mark Internship Complete'
                      });
                    } else {
                      pendingActions.push({
                        type: 'info',
                        priority: 'low',
                        student: student,
                        message: `${student.name} - Internship outreach in progress (Day ${outreachDays}/10).`,
                        action: null
                      });
                    }
                  }
                  
                  // Check for certificate generation requests
                  if (student.certificate_status === 'pending_generation') {
                    pendingActions.push({
                      type: 'certificate_generation',
                      priority: 'high',
                      student: student,
                      message: `${student.name} - Certificate needs to be generated`,
                      action: 'Generate & Send Certificate'
                    });
                  }
                  
                  // Check for Megamind eligibility review
                  if (student.megamind_data?.eligibility_submitted && !student.megamind_data?.eligibility_approved) {
                    const eligibilityReviewed = student.megamind_data?.eligibility?.admin_review;
                    if (!eligibilityReviewed) {
                      pendingActions.push({
                        type: 'megamind_eligibility',
                        priority: 'high',
                        student: student,
                        message: `${student.name} - Megamind eligibility documents need review`,
                        action: 'Review Documents'
                      });
                    }
                  }
                  
                  // Check for locked students
                  if (student.is_locked) {
                    pendingActions.push({
                      type: 'locked',
                      priority: 'low',
                      student: student,
                      message: `${student.name} - Account is locked`,
                      action: 'Unlock'
                    });
                  }
                  
                  // Check for mock call requests (URGENT)
                  if (student.interviews && student.interviews.length > 0) {
                    student.interviews.forEach(interview => {
                      if (interview.status === 'mock_requested') {
                        pendingActions.push({
                          type: 'mock_request',
                          priority: 'high',  // URGENT
                          student: student,
                          interview: interview,
                          message: `🔴 URGENT: ${student.name} - Mock call for ${interview.company_name} (${interview.role})`,
                          action: `Interview on ${new Date(interview.interview_date).toLocaleDateString()}`
                        });
                      }
                    });
                  }
                  
                  // Check for offer letter submissions pending review
                  if (student.megamind_data?.offer_letter?.submitted && 
                      student.megamind_data?.offer_letter?.status === 'pending_review') {
                    pendingActions.push({
                      type: 'offer_letter',
                      priority: 'high',
                      student: student,
                      offerLetter: student.megamind_data.offer_letter,
                      message: `🎉 ${student.name} - Offer letter from ${student.megamind_data.offer_letter.company_name}`,
                      action: `Role: ${student.megamind_data.offer_letter.role}`
                    });
                  }
                });
                
                if (pendingActions.length === 0) return null;
                
                // Sort by priority
                const priorityOrder = { high: 0, medium: 1, low: 2 };
                pendingActions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
                
                return (
                  <Card className="mb-6 border-amber-300 bg-amber-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Pending Actions ({pendingActions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {pendingActions.map((action, idx) => (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              action.priority === 'high' ? 'bg-red-100 border border-red-200' :
                              action.priority === 'medium' ? 'bg-amber-100 border border-amber-200' :
                              'bg-slate-100 border border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                action.priority === 'high' ? 'bg-red-500' :
                                action.priority === 'medium' ? 'bg-amber-500' :
                                'bg-slate-500'
                              }`} />
                              <div>
                                <p className="font-medium text-slate-800">{action.message}</p>
                                <p className="text-sm text-slate-500">{action.action}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {action.type === 'account_approval' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => approveStudent(action.student.id)}
                                  >
                                    <Check className="h-4 w-4 mr-1" /> Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => rejectStudent(action.student.id)}
                                  >
                                    <X className="h-4 w-4 mr-1" /> Reject
                                  </Button>
                                </>
                              )}
                              {action.type === 'session_request' && (
                                <Button 
                                  size="sm" 
                                  className="bg-amber-600 hover:bg-amber-700"
                                  onClick={() => completeSession(action.student.id)}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Complete Session
                                </Button>
                              )}
                              {action.type === 'step_approval' && (
                                <Button 
                                  size="sm" 
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => {
                                    setSelectedStudent(action.student);
                                    setReviewStep(action.stepNum);
                                    setReviewDialogOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> Review
                                </Button>
                              )}
                              {action.type === 'corrections_verification' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => verifyCorrections(action.student.id)}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Verify
                                </Button>
                              )}
                              {action.type === 'corrections_done' && (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => { 
                                      verifyCorrections(action.student.id);
                                      if (action.notificationId) markNotificationRead(action.notificationId);
                                    }}
                                    data-testid={`review-corrections-${action.student.id}`}
                                  >
                                    <Check className="h-4 w-4 mr-1" /> Review & Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="border-red-500 text-red-600"
                                    onClick={() => {
                                      if (action.notificationId) markNotificationRead(action.notificationId);
                                      toast.success('Rejection noted');
                                    }}
                                  >
                                    <X className="h-4 w-4 mr-1" /> Reject
                                  </Button>
                                </div>
                              )}
                              {action.type === 'internship_approval' && (
                                <div className="space-y-2">
                                  {/* Show submitted link */}
                                  {action.submittedLink && (
                                    <div className="bg-slate-50 p-2 rounded text-sm">
                                      <span className="text-slate-500 font-medium">Submitted: </span>
                                      <a href={action.submittedLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                                        {action.submittedLink}
                                      </a>
                                    </div>
                                  )}
                                  {action.submittedNotes && action.submittedNotes !== action.submittedLink && (
                                    <div className="bg-slate-50 p-2 rounded text-sm">
                                      <span className="text-slate-500 font-medium">Notes: </span>
                                      <span className="text-slate-700">{action.submittedNotes}</span>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => reviewInternshipStep(action.student.id, action.stepNum, 'approve')}
                                    >
                                      <Check className="h-4 w-4 mr-1" /> Approve
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => reviewInternshipStep(action.student.id, action.stepNum, 'revise')}
                                    >
                                      <X className="h-4 w-4 mr-1" /> Revise
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {action.type === 'company_list_upload' && (
                                <Button 
                                  size="sm" 
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => setCompanyListDialog({ open: true, student: action.student })}
                                >
                                  <Upload className="h-4 w-4 mr-1" /> Upload List
                                </Button>
                              )}
                              {action.type === 'certificate_generation' && (
                                <Button 
                                  size="sm" 
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={() => openCertUploadDialog(action.student)}
                                >
                                  <Upload className="h-4 w-4 mr-1" /> Upload Certificate
                                </Button>
                              )}
                              {action.type === 'megamind_eligibility' && (
                                <Button 
                                  size="sm" 
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => openMegamindDocsDialog(action.student)}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> View & Review
                                </Button>
                              )}
                              {action.type === 'locked' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-amber-500 text-amber-600"
                                  onClick={() => unlockStudent(action.student.id)}
                                >
                                  <Unlock className="h-4 w-4 mr-1" /> Unlock
                                </Button>
                              )}
                              {action.type === 'mock_request' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => completeMockCall(action.student.id, action.interview.id, action.interview.company_name)}
                                >
                                  <Phone className="h-4 w-4 mr-1" /> Mark Mock Done
                                </Button>
                              )}
                              {action.type === 'offer_letter' && (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="border-blue-500 text-blue-600"
                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${action.offerLetter.offer_letter_url}`, '_blank')}
                                  >
                                    <Eye className="h-4 w-4 mr-1" /> View
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => reviewOfferLetter(action.student.id, 'approve', action.student.name, action.offerLetter.company_name)}
                                  >
                                    <Check className="h-4 w-4 mr-1" /> Approve & Issue Certificate
                                  </Button>
                                </div>
                              )}
                              {action.type === 'whatsapp_request' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => approveWhatsApp(action.student.id)}
                                  data-testid={`approve-whatsapp-${action.student.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Approve & Send Link
                                </Button>
                              )}
                              {action.type === 'whatsapp_switch_request' && (
                                <Button 
                                  size="sm" 
                                  className="bg-amber-600 hover:bg-amber-700"
                                  onClick={() => approveWhatsAppSwitch(action.student.id)}
                                  data-testid={`approve-whatsapp-switch-${action.student.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Approve Switch
                                </Button>
                              )}
                              {action.type === 'mark_internship_complete' && (
                                <Button 
                                  size="sm" 
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={() => fetchOutreachReview(action.student.id, action.student.name)}
                                  data-testid={`review-outreach-${action.student.id}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> Review Outreach
                                </Button>
                              )}
                              {action.type === 'phase2_whatsapp_request' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => phase2MarkWhatsappSent(action.student.id)}
                                  data-testid={`phase2-whatsapp-sent-${action.student.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" /> Link Sent
                                </Button>
                              )}
                              {action.type === 'phase2_job_profile_request' && (
                                <div className="flex flex-col gap-2">
                                  {phase2DocStudentId === action.student.id ? (
                                    <div className="flex gap-2 items-center">
                                      <Input
                                        value={phase2DocLink}
                                        onChange={(e) => setPhase2DocLink(e.target.value)}
                                        placeholder="https://docs.google.com/..."
                                        className="bg-white/10 border-white/20 text-white text-sm h-8 w-64"
                                        data-testid={`phase2-doc-link-input-${action.student.id}`}
                                      />
                                      <Button 
                                        size="sm" 
                                        className="bg-green-600 hover:bg-green-700"
                                        onClick={() => phase2SendJobProfileLink(action.student.id)}
                                        data-testid={`phase2-doc-link-send-${action.student.id}`}
                                      >
                                        <Send className="h-4 w-4 mr-1" /> Send
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => setPhase2DocStudentId(action.student.id)}
                                      data-testid={`phase2-upload-doc-${action.student.id}`}
                                    >
                                      <Upload className="h-4 w-4 mr-1" /> Upload Document Link
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Outreach Review Panel - Collapsible Accordion */}
              {outreachReviewStudent && (
                <Card className="border-purple-500/30 bg-slate-900" data-testid="outreach-review-panel">
                  <CardHeader 
                    className="flex flex-row items-center justify-between cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
                    onClick={() => {
                      if (outreachReviewData) {
                        setOutreachReviewData(null);
                        setOutreachReviewStudent(null);
                      }
                    }}
                  >
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Send className="h-4 w-4 text-purple-400" />
                      {outreachReviewData?.student_name || outreachReviewStudent.name}
                      <span className="text-xs text-slate-500 font-normal ml-2">
                        {outreachReviewData ? `${outreachReviewData.total_completed}/10 days` : ''}
                      </span>
                    </CardTitle>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${outreachReviewData ? 'rotate-180' : ''}`} />
                  </CardHeader>
                  {outreachReviewLoading && (
                    <CardContent className="pt-0 pb-4">
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-purple-400" />
                        <span className="ml-2 text-sm text-slate-400">Loading...</span>
                      </div>
                    </CardContent>
                  )}
                  {outreachReviewData && (
                    <CardContent className="pt-0">
                      <div className="text-xs text-slate-500 flex gap-4 mb-3 pb-2 border-b border-white/5">
                        <span>Started: {outreachReviewData.outreach_started_at ? new Date(outreachReviewData.outreach_started_at).toLocaleDateString() : 'N/A'}</span>
                        <span>Completed: {outreachReviewData.outreach_completed_at ? new Date(outreachReviewData.outreach_completed_at).toLocaleDateString() : 'In Progress'}</span>
                      </div>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        {outreachReviewData.days.map((day) => (
                          <div 
                            key={day.day} 
                            className={`border rounded-lg p-3 ${day.is_completed ? 'border-green-500/20 bg-green-500/5' : 'border-slate-700 bg-slate-800/50 opacity-50'}`}
                            data-testid={`outreach-review-day-${day.day}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold ${day.is_completed ? 'text-green-400' : 'text-slate-500'}`}>Day {day.day}</span>
                                <span className="text-sm text-white">{day.action}</span>
                                {day.is_completed && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                              </div>
                              {day.submitted_at && <span className="text-[10px] text-slate-500">{new Date(day.submitted_at).toLocaleString()}</span>}
                            </div>
                            {day.is_completed && day.notes && (
                              <div className="mt-2 bg-white/5 rounded p-2">
                                <p className="text-xs text-slate-300 break-all">{day.notes}</p>
                              </div>
                            )}
                            {day.is_completed && (day.screenshot_link || day.mail_trail_link) && (
                              <div className="flex gap-3 mt-1.5">
                                {day.screenshot_link && (
                                  <a href={day.screenshot_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline flex items-center gap-1">
                                    <ExternalLink className="h-3 w-3" /> Screenshot
                                  </a>
                                )}
                                {day.mail_trail_link && (
                                  <a href={day.mail_trail_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline flex items-center gap-1">
                                    <Mail className="h-3 w-3" /> Mail Trail
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {outreachReviewData.total_completed >= 10 && (
                        <div className="flex justify-end pt-3 mt-3 border-t border-white/10">
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => markInternshipComplete(outreachReviewStudent.id)}
                            data-testid={`mark-internship-complete-${outreachReviewStudent.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Approve & Mark Internship Complete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Phase 2: Outreach Tracking */}
              <Card className="mb-6 border-purple-500/20" data-testid="phase2-outreach-tracking">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Send className="h-5 w-5 text-purple-400" />
                    Phase 2: Outreach Tracking
                    {phase2TrackingData && <Badge className="bg-purple-500/20 text-purple-300 ml-2">{phase2TrackingData.length} students</Badge>}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchPhase2Tracking} disabled={phase2TrackingLoading} data-testid="refresh-phase2-tracking">
                    {phase2TrackingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="text-sm">Refresh</span>}
                  </Button>
                </CardHeader>
                <CardContent>
                  {phase2TrackingLoading && !phase2TrackingData ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                    </div>
                  ) : phase2TrackingData && phase2TrackingData.length > 0 ? (
                    <>
                      <div className="mb-3">
                        <Input
                          placeholder="Filter by student name..."
                          value={phase2TrackingFilter}
                          onChange={(e) => setPhase2TrackingFilter(e.target.value)}
                          className="max-w-xs bg-white/10 border-white/20 text-white text-sm"
                          data-testid="phase2-tracking-filter"
                        />
                      </div>
                      <div className="overflow-x-auto border border-white/10 rounded-lg">
                        <table className="w-full text-sm" data-testid="phase2-tracking-table">
                          <thead>
                            <tr className="bg-slate-800/80">
                              <th className="sticky left-0 z-10 bg-slate-800 px-4 py-2.5 text-left text-slate-300 font-medium border-r border-white/10 min-w-[160px]">Student</th>
                              <th className="px-2 py-2.5 text-center text-slate-400 font-medium min-w-[80px] border-r border-white/5">Progress</th>
                              {Array.from({length: 14}, (_, i) => i + 1).map(day => (
                                <th key={day} className="px-2 py-2.5 text-center text-slate-400 font-medium min-w-[100px] border-r border-white/5">Day {day}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {phase2TrackingData
                              .filter(s => !phase2TrackingFilter || s.name.toLowerCase().includes(phase2TrackingFilter.toLowerCase()))
                              .map((student) => (
                              <tr key={student.id} className="border-t border-white/5 hover:bg-white/5">
                                <td className="sticky left-0 z-10 bg-slate-900 px-4 py-2 border-r border-white/10">
                                  <div className="font-medium text-white text-xs">{student.name}</div>
                                  <div className="text-[10px] text-slate-500">{student.email}</div>
                                </td>
                                <td className="px-2 py-2 text-center border-r border-white/5">
                                  <span className={`text-xs font-bold ${student.is_completed ? 'text-green-400' : 'text-purple-400'}`}>
                                    {student.completed_count}/14
                                  </span>
                                </td>
                                {Array.from({length: 14}, (_, i) => i + 1).map(day => {
                                  const dayData = student.days[day] || {};
                                  const hasProofs = dayData.screenshot_link || dayData.mail_trail_link || dayData.linkedin_screenshot_link;
                                  return (
                                    <td key={day} className={`px-2 py-1.5 text-center border-r border-white/5 ${dayData.completed ? 'bg-green-500/5' : ''}`}>
                                      {dayData.completed ? (
                                        <div className="space-y-1">
                                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mx-auto" />
                                          {hasProofs && (
                                            <div className="flex flex-col gap-0.5">
                                              {dayData.screenshot_link && (
                                                <a href={dayData.screenshot_link} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-400 hover:underline truncate block max-w-[90px]">Screenshot</a>
                                              )}
                                              {dayData.mail_trail_link && (
                                                <a href={dayData.mail_trail_link} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-400 hover:underline truncate block max-w-[90px]">Mail Trail</a>
                                              )}
                                              {dayData.linkedin_screenshot_link && (
                                                <a href={dayData.linkedin_screenshot_link} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-400 hover:underline truncate block max-w-[90px]">LinkedIn</a>
                                              )}
                                            </div>
                                          )}
                                          {dayData.notes && (
                                            <p className="text-[9px] text-slate-500 truncate max-w-[90px]" title={dayData.notes}>{dayData.notes}</p>
                                          )}
                                          {dayData.submitted_at && (
                                            <p className="text-[8px] text-slate-600">{new Date(dayData.submitted_at).toLocaleDateString()}</p>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-slate-600 text-[10px]">—</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No students have started Phase 2 outreach yet.</p>
                  )}
                </CardContent>
              </Card>

              {/* Today's Attendance Summary */}
              {attendanceReport && (
                <Card className="mb-6 border-emerald-200 bg-emerald-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                      <Clock className="h-5 w-5" />
                      Today's Attendance - {attendanceReport.date}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6 mb-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-emerald-600">{attendanceReport.logged_in_today}</p>
                        <p className="text-xs text-emerald-700">Currently Logged In</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">{attendanceReport.completed_today}</p>
                        <p className="text-xs text-green-700">Completed Today</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-600">{attendanceReport.total_students}</p>
                        <p className="text-xs text-slate-500">Total Students</p>
                      </div>
                      <Button
                        onClick={fetchAttendanceReport}
                        variant="outline"
                        size="sm"
                        className="ml-auto border-emerald-500 text-emerald-600 hover:bg-emerald-100"
                      >
                        Refresh
                      </Button>
                    </div>
                    
                    {/* Attendance Details Table */}
                    {attendanceReport.report.length > 0 && (
                      <div className="overflow-x-auto max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-emerald-100 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-emerald-800">Student</th>
                              <th className="text-center p-2 text-emerald-800">Today</th>
                              <th className="text-center p-2 text-emerald-800">Hours</th>
                              <th className="text-center p-2 text-emerald-800">Total Days</th>
                              <th className="text-center p-2 text-emerald-800">Avg Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attendanceReport.report.map((r, idx) => (
                              <tr key={idx} className="border-t border-emerald-200">
                                <td className="p-2">{r.student_name}</td>
                                <td className="p-2 text-center">
                                  {r.is_logged_in ? (
                                    <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                      Online
                                    </span>
                                  ) : r.today_status === 'completed' ? (
                                    <span className="text-emerald-600">✓ Done</span>
                                  ) : r.today_status === 'insufficient_hours' ? (
                                    <span className="text-amber-600">Incomplete</span>
                                  ) : (
                                    <span className="text-slate-400">Not logged in</span>
                                  )}
                                </td>
                                <td className="p-2 text-center">{r.today_hours?.toFixed(1) || '-'}</td>
                                <td className="p-2 text-center">{r.total_days_completed}</td>
                                <td className="p-2 text-center">{r.average_hours?.toFixed(1) || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Students Table */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Students</CardTitle>
                  <Button
                    onClick={fetchPlacementStudents}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-600 hover:bg-slate-50"
                    disabled={placementLoading}
                  >
                    {placementLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                          <path d="M3 3v5h5" />
                          <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                          <path d="M16 21h5v-5" />
                        </svg>
                        Refresh
                      </>
                    )}
                  </Button>
                </CardHeader>
                <CardContent>
                  {placementLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                    </div>
                  ) : placementStudents.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Zap className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No students registered yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Step</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Locked</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {placementStudents.map((student) => (
                          <TableRow key={student.id}>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell className="text-sm">{student.email}</TableCell>
                            <TableCell>
                              {student.status === 'pending' && (
                                <Badge className="bg-amber-500">Pending</Badge>
                              )}
                              {student.status === 'approved' && (
                                <Badge className="bg-green-500">Approved</Badge>
                              )}
                              {student.status === 'rejected' && (
                                <Badge className="bg-red-500">Rejected</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Step {student.current_step}/6</span>
                                <Progress value={(student.current_step / 6) * 100} className="w-16 h-2" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`font-bold ${student.scores?.total >= 75 ? 'text-green-600' : 'text-slate-700'}`}>
                                {student.scores?.total || 0}/100
                              </span>
                            </TableCell>
                            <TableCell>
                              {student.is_locked ? (
                                <Badge variant="destructive" className="bg-red-100 text-red-700">
                                  <Lock className="h-3 w-3 mr-1" /> Locked
                                </Badge>
                              ) : (
                                <span className="text-green-600 text-sm">Active</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {student.status === 'pending' && (
                                    <>
                                      <DropdownMenuItem onClick={() => approveStudent(student.id)}>
                                        <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                        Approve
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => rejectStudent(student.id)}>
                                        <XCircle className="mr-2 h-4 w-4 text-red-600" />
                                        Reject
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {student.status === 'approved' && (
                                    <>
                                      <DropdownMenuItem onClick={() => {
                                        setSelectedStudent(student);
                                        setReviewDialogOpen(true);
                                      }}>
                                        <Eye className="mr-2 h-4 w-4" />
                                        Review Submissions
                                      </DropdownMenuItem>
                                      {student.current_step === 3 && student.submissions?.step_3?.status === 'requested' && (
                                        <DropdownMenuItem onClick={() => completeSession(student.id)}>
                                          <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                          Complete 1:1 Session
                                        </DropdownMenuItem>
                                      )}
                                      {student.current_step === 4 && student.submissions?.step_4?.status === 'pending_verification' && (
                                        <DropdownMenuItem onClick={() => verifyCorrections(student.id)}>
                                          <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                          Verify Corrections
                                        </DropdownMenuItem>
                                      )}
                                      {!student.is_locked ? (
                                        <DropdownMenuItem onClick={() => lockStudent(student.id)}>
                                          <Lock className="mr-2 h-4 w-4 text-red-600" />
                                          Lock Account
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem onClick={() => unlockStudent(student.id)}>
                                          <Unlock className="mr-2 h-4 w-4 text-green-600" />
                                          Unlock (Reset to Step 1)
                                        </DropdownMenuItem>
                                      )}
                                      {/* Change Path Options - only for approved students with a decision */}
                                      {student.decision_path && (
                                        <>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuLabel className="text-xs text-slate-500">Change Path</DropdownMenuLabel>
                                          {student.decision_path !== 'internship' && (
                                            <DropdownMenuItem onClick={() => resetDecisionPath(student.id, student.name, 'internship')}>
                                              <Briefcase className="mr-2 h-4 w-4 text-blue-600" />
                                              Switch to Internship + Outreach
                                            </DropdownMenuItem>
                                          )}
                                          {student.decision_path !== 'direct_outreach' && (
                                            <DropdownMenuItem onClick={() => resetDecisionPath(student.id, student.name, 'direct_outreach')}>
                                              <Send className="mr-2 h-4 w-4 text-orange-600" />
                                              Switch to Direct Outreach
                                            </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem onClick={() => resetDecisionPath(student.id, student.name, null)}>
                                            <RotateCcw className="mr-2 h-4 w-4 text-purple-600" />
                                            Reset Decision (Choose Again)
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </>
                                  )}
                                  {/* Delete option available for all students */}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => deleteStudent(student.id, student.name)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete Student
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Internship Metrics Section */}
              {internshipMetrics.length > 0 && (
                <Card className="mb-6 border-blue-200 bg-blue-50" data-testid="internship-metrics-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                        <TrendingUp className="h-5 w-5" />
                        Internship Metrics ({internshipMetrics.length} students)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {internshipMetrics.map((s, idx) => {
                        const m = s.internship_metrics || {};
                        const isExpanded = expandedMetricsStudents[idx];
                        return (
                          <div key={idx} className="bg-white rounded-lg border border-blue-200 overflow-hidden" data-testid={`metrics-student-${idx}`}>
                            <div 
                              className="flex items-center justify-between p-4 cursor-pointer hover:bg-blue-50/50 transition-colors"
                              onClick={() => setExpandedMetricsStudents(prev => ({ ...prev, [idx]: !prev[idx] }))}
                              data-testid={`metrics-toggle-${idx}`}
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-blue-500" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-blue-500" />
                                )}
                                <div>
                                  <h4 className="font-semibold text-slate-800">{s.name}</h4>
                                  {!isExpanded && <p className="text-xs text-slate-400">{s.email}</p>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <Badge className="bg-blue-500">Metrics Submitted</Badge>
                                <Button
                                  onClick={() => downloadMetricsCSV(s)}
                                  variant="outline"
                                  size="sm"
                                  className="border-blue-500 text-blue-600 hover:bg-blue-100"
                                  data-testid={`download-metrics-csv-${idx}`}
                                >
                                  <Download className="h-4 w-4 mr-1" /> Download CSV
                                </Button>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="px-4 pb-4 pt-0 border-t border-blue-100">
                                <p className="text-sm text-slate-500 mb-3">{s.email}</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Companies Outreached</p>
                                    <p className="font-bold text-slate-800">{m.companies_outreached || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Total Leads Generated</p>
                                    <p className="font-bold text-slate-800">{m.total_leads_generated || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">LinkedIn Outreach</p>
                                    <p className="font-bold text-slate-800">{m.total_linkedin_outreach || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Email Outreach</p>
                                    <p className="font-bold text-slate-800">{m.total_email_outreach || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Call Outreach</p>
                                    <p className="font-bold text-slate-800">{m.total_call_outreach || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Meetings Set</p>
                                    <p className="font-bold text-slate-800">{m.meetings_set || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Responses Received</p>
                                    <p className="font-bold text-slate-800">{m.responses_received || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Positive Replies</p>
                                    <p className="font-bold text-slate-800">{m.positive_replies || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Hours Spent</p>
                                    <p className="font-bold text-slate-800">{m.total_hours_spent || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2">
                                    <p className="text-slate-500 text-xs">Market Focused</p>
                                    <p className="font-bold text-slate-800">{m.market_focused || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2 col-span-2">
                                    <p className="text-slate-500 text-xs">Tools Used</p>
                                    <p className="font-bold text-slate-800">{m.tools_used || '-'}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

            </>
          )}

          {/* Forgot Password Requests Section - Placement AI */}
          {activeTab === 'placement' && placementForgotRequests.filter(r => r.status === 'pending').length > 0 && (
            <Card className="mt-6 border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-amber-800 flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Password Reset Requests ({placementForgotRequests.filter(r => r.status === 'pending').length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {placementForgotRequests.filter(r => r.status === 'pending').map((request) => (
                    <div key={request.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-slate-900">{request.name}</p>
                        <p className="text-sm text-slate-500">{request.email}</p>
                        <p className="text-xs text-slate-400">
                          Requested: {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setResetPasswordEmail(request.email);
                            setResetModule('placement');
                            setShowResetModal(true);
                          }}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          Reset Password
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => dismissForgotRequest(request.id, 'placement')}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certificate Upload Dialog */}
          {showCertUploadDialog && certUploadStudent && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle>Upload Certificate for {certUploadStudent.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Certificate PDF File</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setCertificateFile(e.target.files[0])}
                      className="mt-1"
                    />
                  </div>
                  {certificateFile && (
                    <p className="text-sm text-green-600">
                      Selected: {certificateFile.name}
                    </p>
                  )}
                  <p className="text-sm text-slate-500">
                    Upload the certificate as a PDF file. The student will be able to download it once uploaded.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setShowCertUploadDialog(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => generateCertificate(certUploadStudent.id)}
                      disabled={!certificateFile}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Certificate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Megamind Documents Review Dialog */}
          {showMegamindDocsDialog && megamindDocsStudent && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <CardHeader>
                  <CardTitle>Megamind Eligibility Documents - {megamindDocsStudent.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {megamindDocsStudent.megamind_data?.eligibility ? (
                    <>
                      <div className="space-y-3">
                        {/* Portfolio Link */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label className="text-sm text-slate-500">Portfolio Link</Label>
                          {megamindDocsStudent.megamind_data.eligibility.portfolio_link ? (
                            <a 
                              href={megamindDocsStudent.megamind_data.eligibility.portfolio_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline block mt-1"
                            >
                              {megamindDocsStudent.megamind_data.eligibility.portfolio_link}
                            </a>
                          ) : (
                            <span className="text-slate-400 mt-1 block">Not provided</span>
                          )}
                        </div>
                        
                        {/* Company Sheet Link */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label className="text-sm text-slate-500">Company Sheet Link</Label>
                          {megamindDocsStudent.megamind_data.eligibility.companies_list_link ? (
                            <a 
                              href={megamindDocsStudent.megamind_data.eligibility.companies_list_link}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline mt-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              {megamindDocsStudent.megamind_data.eligibility.companies_list_link}
                            </a>
                          ) : (
                            <span className="text-slate-400 mt-1 block">Not provided</span>
                          )}
                        </div>
                        
                        {/* People Sheet Link */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label className="text-sm text-slate-500">People Sheet Link</Label>
                          {megamindDocsStudent.megamind_data.eligibility.prospect_list_link ? (
                            <a 
                              href={megamindDocsStudent.megamind_data.eligibility.prospect_list_link}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline mt-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              {megamindDocsStudent.megamind_data.eligibility.prospect_list_link}
                            </a>
                          ) : (
                            <span className="text-slate-400 mt-1 block">Not provided</span>
                          )}
                        </div>
                        
                        {/* Market Research Link */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label className="text-sm text-slate-500">Market Research Link</Label>
                          {megamindDocsStudent.megamind_data.eligibility.market_research_link ? (
                            <a 
                              href={megamindDocsStudent.megamind_data.eligibility.market_research_link}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline mt-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              {megamindDocsStudent.megamind_data.eligibility.market_research_link}
                            </a>
                          ) : (
                            <span className="text-slate-400 mt-1 block">Not provided</span>
                          )}
                        </div>
                        
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label className="text-sm text-slate-500">Submitted At</Label>
                          <p className="mt-1">
                            {megamindDocsStudent.megamind_data.eligibility.submitted_at 
                              ? new Date(megamindDocsStudent.megamind_data.eligibility.submitted_at).toLocaleString()
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Rejection Reason Input */}
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <Label className="text-sm text-red-600">Rejection Reason (if rejecting)</Label>
                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Enter reason for rejection so student can correct and resubmit..."
                          className="w-full mt-2 p-2 border rounded-md text-sm"
                          rows={3}
                        />
                      </div>
                      
                      <div className="flex gap-2 justify-end pt-4 border-t">
                        <Button variant="outline" onClick={() => setShowMegamindDocsDialog(false)}>
                          Cancel
                        </Button>
                        <Button 
                          variant="destructive"
                          onClick={() => reviewMegamindEligibility(megamindDocsStudent.id, 'reject', rejectionReason)}
                          disabled={!rejectionReason.trim()}
                        >
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => reviewMegamindEligibility(megamindDocsStudent.id, 'approve')}
                        >
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-500">No eligibility documents found.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Cold Call Lab Tab Content */}
          {activeTab === 'coldcall' && (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
                    <Phone className="h-8 w-8 text-cyan-500" />
                    Cold Call Lab
                  </h1>
                  <p className="text-slate-500 mt-1">Manage and approve Cold Call Lab users</p>
                </div>
                <Button
                  onClick={fetchColdcallUsers}
                  variant="outline"
                  className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                  data-testid="refresh-coldcall-btn"
                >
                  Refresh
                </Button>
              </div>

              {/* Stats Overview */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-slate-500 mb-1">Total Users</p>
                    <p className="text-2xl font-bold text-slate-900">{coldcallStats?.total || coldcallUsers.length}</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-amber-600 mb-1">Pending Approval</p>
                    <p className="text-2xl font-bold text-amber-600">{coldcallStats?.pending || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-green-600 mb-1">Approved</p>
                    <p className="text-2xl font-bold text-green-600">{coldcallStats?.approved || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-red-600 mb-1">Rejected</p>
                    <p className="text-2xl font-bold text-red-600">{coldcallStats?.rejected || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-cyan-200 bg-cyan-50">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-cyan-600 mb-1">Total Calls</p>
                    <p className="text-2xl font-bold text-cyan-600">{coldcallStats?.total_calls || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Pending Users */}
              {coldcallUsers.filter(u => u.status === 'pending').length > 0 && (
                <Card className="mb-6 border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-amber-600 flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Pending Approval ({coldcallUsers.filter(u => u.status === 'pending').length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Registered</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coldcallUsers.filter(u => u.status === 'pending').map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => approveColdcallUser(user.id)}
                                  data-testid={`approve-${user.id}`}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => rejectColdcallUser(user.id)}
                                  data-testid={`reject-${user.id}`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* All Users Table */}
              <Card>
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                </CardHeader>
                <CardContent>
                  {coldcallLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
                    </div>
                  ) : coldcallUsers.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      No users registered yet
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Total Calls</TableHead>
                          <TableHead>Avg Score</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coldcallUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">{user.name}</TableCell>
                            <TableCell className="text-slate-500">{user.email}</TableCell>
                            <TableCell>
                              <Badge className={
                                user.status === 'approved' ? 'bg-green-100 text-green-700' :
                                user.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {user.status || 'pending'}
                              </Badge>
                            </TableCell>
                            <TableCell>{user.total_calls || 0}</TableCell>
                            <TableCell>
                              <span className={`font-bold ${
                                (user.avg_score || 0) >= 80 ? 'text-green-600' :
                                (user.avg_score || 0) >= 60 ? 'text-amber-600' : 'text-slate-600'
                              }`}>
                                {user.avg_score || 0}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-slate-500">
                                {user.categories_practiced?.length || 0}/7 cats, {user.personas_practiced?.length || 0}/6 personas
                              </span>
                            </TableCell>
                            <TableCell>
                              {user.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-green-500 text-green-600 hover:bg-green-50"
                                    onClick={() => approveColdcallUser(user.id)}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-500 text-red-600 hover:bg-red-50"
                                    onClick={() => rejectColdcallUser(user.id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                              {user.status === 'approved' && (
                                <span className="text-green-600 text-sm">Active</span>
                              )}
                              {user.status === 'rejected' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-500 text-green-600 hover:bg-green-50"
                                  onClick={() => approveColdcallUser(user.id)}
                                >
                                  Re-approve
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Forgot Password Requests Section - Cold Call Lab */}
              {coldcallForgotRequests.filter(r => r.status === 'pending').length > 0 && (
                <Card className="mt-6 border-cyan-200 bg-cyan-50">
                  <CardHeader>
                    <CardTitle className="text-cyan-800 flex items-center gap-2">
                      <Key className="h-5 w-5" />
                      Password Reset Requests ({coldcallForgotRequests.filter(r => r.status === 'pending').length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {coldcallForgotRequests.filter(r => r.status === 'pending').map((request) => (
                        <div key={request.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                          <div>
                            <p className="font-medium text-slate-900">{request.name}</p>
                            <p className="text-sm text-slate-500">{request.email}</p>
                            <p className="text-xs text-slate-400">
                              Requested: {new Date(request.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => {
                                setResetPasswordEmail(request.email);
                                setResetModule('coldcall');
                                setShowResetModal(true);
                              }}
                              className="bg-cyan-500 hover:bg-cyan-600"
                            >
                              Reset Password
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => dismissForgotRequest(request.id, 'coldcall')}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <p className="text-sm text-slate-500">Set a new password for {resetPasswordEmail}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use a simple password that you can easily share with the user
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowResetModal(false); setNewPassword(''); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={resetUserPassword}
                  disabled={!newPassword}
                  className={resetModule === 'placement' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-cyan-500 hover:bg-cyan-600'}
                >
                  Reset Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[85vh] flex flex-col">
            <CardHeader className="flex-shrink-0 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-6 w-6 text-amber-500" />
                  Placement AI Leaderboard
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowLeaderboard(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              {leaderboardData && (
                <p className="text-sm text-slate-500">
                  {leaderboardData.total_participants} participants
                </p>
              )}
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {leaderboardLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                </div>
              ) : leaderboardData?.leaderboard?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-16 text-center">Rank</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead className="text-center">Path</TableHead>
                      <TableHead className="text-center">Progress</TableHead>
                      <TableHead className="text-center">Attendance</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                      <TableHead className="text-center">Badge</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboardData.leaderboard.map((entry, index) => (
                      <TableRow key={entry.student_id} className={index < 3 ? 'bg-amber-50' : ''}>
                        <TableCell className="text-center font-bold">
                          {entry.rank === 1 && <span className="text-2xl">🥇</span>}
                          {entry.rank === 2 && <span className="text-2xl">🥈</span>}
                          {entry.rank === 3 && <span className="text-2xl">🥉</span>}
                          {entry.rank > 3 && <span className="text-slate-600">#{entry.rank}</span>}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{entry.name}</p>
                            <p className="text-xs text-slate-500">{entry.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.decision_path === 'internship' ? (
                            <Badge className="bg-blue-100 text-blue-700">Internship</Badge>
                          ) : entry.decision_path === 'direct_outreach' ? (
                            <Badge className="bg-orange-100 text-orange-700">Direct</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="text-sm">
                            <p>Step {entry.current_step}/4</p>
                            {entry.outreach_progress > 0 && (
                              <p className="text-xs text-orange-600">Outreach: {entry.outreach_progress}/10</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="text-sm">
                            <p>{entry.attendance_days} days</p>
                            <p className="text-xs text-slate-500">{entry.attendance_hours}h total</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold text-lg">{entry.combined_score}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            entry.badge === 'Champion' ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white' :
                            entry.badge === 'Gold' ? 'bg-yellow-100 text-yellow-700' :
                            entry.badge === 'Silver' ? 'bg-slate-200 text-slate-700' :
                            entry.badge === 'Bronze' ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {entry.badge}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.is_online ? (
                            <span className="flex items-center justify-center gap-1 text-green-600 text-sm">
                              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                              Online
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">Offline</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  No participants yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Dialog for Placement AI */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Student: {selectedStudent?.name}</DialogTitle>
            <DialogDescription>
              Review and approve/reject student submissions
            </DialogDescription>
          </DialogHeader>
          
          {selectedStudent && (
            <div className="space-y-4 mt-4">
              {/* Student Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Email:</span> {selectedStudent.email}
                  </div>
                  <div>
                    <span className="text-slate-500">Phone:</span> {selectedStudent.phone}
                  </div>
                  <div>
                    <span className="text-slate-500">Experience:</span> {selectedStudent.years_of_experience}
                  </div>
                  <div>
                    <span className="text-slate-500">Score:</span> {selectedStudent.scores?.total}/100
                  </div>
                </div>
              </div>

              {/* Step Submissions */}
              <div className="space-y-3">
                <h4 className="font-semibold">Submissions</h4>
                
                {/* Step 1 - Portfolio Detail Submission */}
                {selectedStudent.submissions?.step_1 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Step 1: Portfolio Detail Submission</span>
                      <Badge className={
                        selectedStudent.submissions.step_1.status === 'approved' || selectedStudent.submissions.step_1.status === 'approve' ? 'bg-green-500' :
                        selectedStudent.submissions.step_1.status === 'pending_review' ? 'bg-amber-500' : 'bg-red-500'
                      }>
                        {selectedStudent.submissions.step_1.status}
                      </Badge>
                    </div>
                    
                    {/* Portfolio Links Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-3">
                      {selectedStudent.submissions.step_1.professional_photos_link && (
                        <a href={selectedStudent.submissions.step_1.professional_photos_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Professional Photos
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.video_resume_link && (
                        <a href={selectedStudent.submissions.step_1.video_resume_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Video Resume
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.certificates_link && (
                        <a href={selectedStudent.submissions.step_1.certificates_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Certificates
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.market_research_link && (
                        <a href={selectedStudent.submissions.step_1.market_research_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Market Research
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.sales_playbook_link && (
                        <a href={selectedStudent.submissions.step_1.sales_playbook_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Sales Playbook
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.cold_call_recording_link && (
                        <a href={selectedStudent.submissions.step_1.cold_call_recording_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Cold Call Recording
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.linkedin_profile_url && (
                        <a href={selectedStudent.submissions.step_1.linkedin_profile_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> LinkedIn Profile
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.resume_link && (
                        <a href={selectedStudent.submissions.step_1.resume_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Resume PDF
                        </a>
                      )}
                      {selectedStudent.submissions.step_1.past_performance_report && (
                        <div className="mt-2 text-sm bg-slate-50 p-2 rounded">
                          <p className="text-slate-500 font-medium">Past Performance Report (Achievements, Metrics, Awards):</p>
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedStudent.submissions.step_1.past_performance_report}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Contact Info */}
                    {(selectedStudent.submissions.step_1.contact_email || selectedStudent.submissions.step_1.contact_phone) && (
                      <div className="text-sm text-slate-600 mb-2">
                        {selectedStudent.submissions.step_1.contact_email && <span className="mr-4">📧 {selectedStudent.submissions.step_1.contact_email}</span>}
                        {selectedStudent.submissions.step_1.contact_phone && <span>📞 {selectedStudent.submissions.step_1.contact_phone}</span>}
                      </div>
                    )}
                    
                    {/* Why Hire Me */}
                    {selectedStudent.submissions.step_1.why_hire_me && (
                      <div className="mt-2 text-sm bg-slate-50 p-2 rounded">
                        <p className="text-slate-500 font-medium">Why Hire Me (1-2 paragraphs):</p>
                        <p className="text-slate-700 whitespace-pre-wrap">{selectedStudent.submissions.step_1.why_hire_me}</p>
                      </div>
                    )}
                    
                    {/* Past Experiences */}
                    {selectedStudent.submissions.step_1.past_experiences && selectedStudent.submissions.step_1.past_experiences.length > 0 && (
                      <div className="mt-2 text-sm">
                        <p className="text-slate-500 font-medium mb-1">Past Experience:</p>
                        <div className="space-y-1">
                          {selectedStudent.submissions.step_1.past_experiences.map((exp, idx) => (
                            <div key={idx} className="bg-slate-50 p-2 rounded text-slate-700">
                              <span className="font-medium">{exp.role}</span> at <span className="font-medium">{exp.company}</span> ({exp.duration})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Hobbies & Interests */}
                    {selectedStudent.submissions.step_1.outside_work && (
                      <div className="mt-2 text-sm">
                        <p className="text-slate-500 font-medium">Hobbies & Interests:</p>
                        <p className="text-slate-700 whitespace-pre-wrap">{selectedStudent.submissions.step_1.outside_work}</p>
                      </div>
                    )}
                    
                    {/* Needs Resubmit Notice */}
                    {selectedStudent.submissions.step_1.needs_resubmit && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
                        ⚠️ Resubmission requested. Waiting for student to update.
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    {selectedStudent.submissions.step_1.status === 'pending_review' && !selectedStudent.submissions.step_1.needs_resubmit && (
                      <div className="mt-3 flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => reviewStudentStep(selectedStudent.id, 1, 'approve')}
                        >
                          ✓ Approve Portfolio
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => {
                            setReviewStep(1);
                          }}
                        >
                          ✗ Request Changes
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2 - Admin Review (Approval Step) */}
                {selectedStudent.current_step >= 2 && selectedStudent.submissions?.step_1?.status !== 'pending_review' && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Step 2: Admin Review</span>
                      <Badge className={
                        selectedStudent.current_step > 2 ? 'bg-green-500' :
                        selectedStudent.current_step === 2 ? 'bg-amber-500' : 'bg-slate-400'
                      }>
                        {selectedStudent.current_step > 2 ? 'Approved' : selectedStudent.current_step === 2 ? 'Pending Approval' : 'Locked'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      Review the portfolio submission above and approve to unlock the next step for the student.
                    </p>
                    {selectedStudent.current_step === 2 && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => reviewStudentStep(selectedStudent.id, 2, 'approve')}
                        >
                          ✓ Complete Admin Review & Unlock Step 3
                        </Button>
                      </div>
                    )}
                    {selectedStudent.current_step > 2 && (
                      <p className="text-sm text-green-600">✓ Admin review completed. Student can proceed to Step 3.</p>
                    )}
                  </div>
                )}

                {/* Step 3 - One-on-One Session */}
                {selectedStudent.current_step >= 3 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Step 3: One-on-One Session</span>
                      <Badge className={
                        selectedStudent.submissions?.step_3?.status === 'completed' ? 'bg-green-500' :
                        selectedStudent.submissions?.step_3?.status === 'requested' ? 'bg-amber-500' : 'bg-slate-400'
                      }>
                        {selectedStudent.submissions?.step_3?.status === 'completed' ? 'Completed' :
                         selectedStudent.submissions?.step_3?.status === 'requested' ? 'Session Requested' : 'Waiting'}
                      </Badge>
                    </div>
                    
                    {selectedStudent.submissions?.step_3?.status === 'requested' && (
                      <>
                        <p className="text-sm text-slate-600 mb-3">
                          Student has requested a 1:1 session on {new Date(selectedStudent.submissions.step_3.requested_at).toLocaleDateString()}.
                          <br/>Schedule and complete the session, then click the button below.
                        </p>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => completeSession(selectedStudent.id)}
                        >
                          ✓ Mark Session as Completed
                        </Button>
                      </>
                    )}
                    
                    {selectedStudent.submissions?.step_3?.status === 'completed' && (
                      <p className="text-sm text-green-600">
                        ✓ Session completed on {new Date(selectedStudent.submissions.step_3.completed_at).toLocaleDateString()}. 
                        Student has moved to Step 4.
                      </p>
                    )}
                    
                    {!selectedStudent.submissions?.step_3 && selectedStudent.current_step === 3 && (
                      <p className="text-sm text-slate-500">Waiting for student to request a session.</p>
                    )}
                  </div>
                )}

                {/* Step 4 - Corrections Confirmation */}
                {selectedStudent.current_step >= 4 && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Step 4: Corrections Confirmation</span>
                      <Badge className={
                        selectedStudent.submissions?.step_4?.status === 'verified' ? 'bg-green-500' :
                        selectedStudent.submissions?.step_4?.status === 'pending_verification' ? 'bg-amber-500' : 'bg-slate-400'
                      }>
                        {selectedStudent.submissions?.step_4?.status === 'verified' ? 'Verified' :
                         selectedStudent.submissions?.step_4?.status === 'pending_verification' ? 'Pending Verification' : 'Waiting'}
                      </Badge>
                    </div>
                    
                    {selectedStudent.submissions?.step_4?.status === 'pending_verification' && (
                      <>
                        <p className="text-sm text-slate-600 mb-3">
                          Student has confirmed corrections are complete. Review and verify.
                        </p>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => verifyCorrections(selectedStudent.id)}
                        >
                          ✓ Verify Corrections Complete
                        </Button>
                      </>
                    )}
                    
                    {selectedStudent.submissions?.step_4?.status === 'verified' && (
                      <p className="text-sm text-green-600">✓ Workflow completed!</p>
                    )}
                    
                    {!selectedStudent.submissions?.step_4 && selectedStudent.current_step === 4 && (
                      <p className="text-sm text-slate-500">Waiting for student to confirm corrections.</p>
                    )}
                  </div>
                )}

                {/* Rejection Comments */}
                {reviewStep && (
                  <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                    <Label>Rejection Comments for Step {reviewStep}</Label>
                    <Textarea
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      placeholder="Enter feedback for the student..."
                      className="mt-2"
                    />
                    <div className="mt-3 flex gap-2">
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => reviewStudentStep(selectedStudent.id, reviewStep, 'reject')}
                      >
                        Submit Rejection
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setReviewStep(null); setReviewComments(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}
            </DialogTitle>
            <DialogDescription>
              {editingCandidate 
                ? 'Update candidate details and placement status' 
                : 'Add a new trained candidate to the portal'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Candidate Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Mohammed Soheb"
                required
                data-testid="candidate-name-input"
              />
            </div>

            {/* Photo URL with Preview and Upload Option */}
            <div className="space-y-2">
              <Label htmlFor="photo_url">Photo *</Label>
              <div className="flex gap-3">
                {formData.photo_url && (
                  <div className="flex-shrink-0">
                    <img
                      src={getDirectImageUrl(formData.photo_url)}
                      alt="Preview"
                      className="w-20 h-20 rounded-lg object-cover border border-slate-200"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=100&h=100&fit=crop';
                      }}
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  {/* Upload Photo Button */}
                  <div 
                    className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:border-hfs-navy transition-colors cursor-pointer bg-slate-50"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={photoInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    {photoUploading ? (
                      <div className="flex items-center justify-center gap-2 text-slate-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 text-slate-600">
                        <ImageIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Click to upload photo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* OR Divider */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400">OR paste URL</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  
                  {/* URL Input */}
                  <Input
                    id="photo_url"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://example.com/photo.jpg"
                    data-testid="candidate-photo-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolio_link">Portfolio Link *</Label>
              <Input
                id="portfolio_link"
                name="portfolio_link"
                value={formData.portfolio_link}
                onChange={handleChange}
                placeholder="https://hireforskillz.my.canva.site/soheb"
                required
                data-testid="candidate-portfolio-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="strength">Strength *</Label>
              <Input
                id="strength"
                name="strength"
                value={formData.strength}
                onChange={handleChange}
                placeholder="SaaS Sales Expert"
                required
                data-testid="candidate-strength-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expected_ctc">Expected CTC (₹)</Label>
              <Input
                id="expected_ctc"
                name="expected_ctc"
                value={formData.expected_ctc}
                onChange={handleChange}
                placeholder="e.g., 12 LPA, 15,00,000"
                data-testid="candidate-ctc-input"
              />
              <p className="text-xs text-slate-500">Enter in INR (e.g., "12 LPA" or "15,00,000")</p>
            </div>

            {editingCandidate && (
              <>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label htmlFor="is_placed">Placement Status</Label>
                    <p className="text-sm text-slate-500">Mark if candidate has been placed</p>
                  </div>
                  <Switch
                    id="is_placed"
                    checked={formData.is_placed}
                    onCheckedChange={handleSwitchChange}
                    data-testid="candidate-placed-switch"
                  />
                </div>

                {formData.is_placed && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-green-600 mb-2">
                      <Building2 className="h-4 w-4" />
                      <span className="font-medium text-sm">Placement Details</span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="placed_company_name">Company Name</Label>
                      <Input
                        id="placed_company_name"
                        name="placed_company_name"
                        value={formData.placed_company_name}
                        onChange={handleChange}
                        placeholder="Scaler Academy"
                        data-testid="candidate-company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="placed_company_logo">Company Logo URL (optional)</Label>
                      <Input
                        id="placed_company_logo"
                        name="placed_company_logo"
                        value={formData.placed_company_logo}
                        onChange={handleChange}
                        placeholder="https://example.com/logo.png"
                        data-testid="candidate-company-logo-input"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-hfs-navy hover:bg-hfs-navy-dark text-white"
                disabled={formLoading}
                data-testid="candidate-submit-btn"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingCandidate ? (
                  'Update Candidate'
                ) : (
                  'Add Candidate'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">
              Delete Candidate
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingCandidate?.name}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={formLoading}
              data-testid="confirm-delete-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-hfs-navy" />
              Upload Candidates from Excel
            </DialogTitle>
            <DialogDescription>
              Upload your Google Form Excel export directly. Only 'Full Name' is required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Expected Format */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 max-h-48 overflow-y-auto">
              <h4 className="font-medium text-slate-900 mb-2">Google Form Columns Supported:</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                <div>• <strong>Full Name</strong> (required)</div>
                <div>• Email ID</div>
                <div>• Contact Number</div>
                <div>• Portfolio Link</div>
                <div>• Total Years of Experience</div>
                <div>• Current Organization</div>
                <div>• Expected CTC</div>
                <div>• Notice Period</div>
                <div>• Preferred Work Mode</div>
                <div>• Willingness to Relocate</div>
                <div>• What kind of roles...</div>
                <div>• Upload a recent photo</div>
                <div>• Already Placed (Yes/No)</div>
              </div>
            </div>

            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-hfs-navy transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                className="hidden"
                data-testid="excel-file-input"
              />
              {uploadLoading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-hfs-navy mb-3" />
                  <p className="text-slate-600">Processing file...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-10 w-10 text-slate-400 mb-3" />
                  <p className="text-slate-600 font-medium">Click to upload Google Form Excel export</p>
                  <p className="text-sm text-slate-400 mt-1">.xlsx or .xls files only</p>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="text-xs text-slate-500">
              <p><strong>What happens:</strong></p>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Candidates matched by Name or Email - updates existing, adds new</li>
                <li>Public info (experience, CTC, work mode) visible to all visitors</li>
                <li>Contact info (email, phone, portfolio) visible only after registration</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
              disabled={uploadLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company List Upload Dialog */}
      <Dialog open={companyListDialog.open} onOpenChange={(open) => setCompanyListDialog({ open, student: open ? companyListDialog.student : null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Company List</DialogTitle>
            <DialogDescription>
              Upload the company list for {companyListDialog.student?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company List URL (Google Drive link)</Label>
              <Input
                type="url"
                value={companyListForm.url}
                onChange={(e) => setCompanyListForm({ ...companyListForm, url: e.target.value })}
                placeholder="https://drive.google.com/..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes for Student (Optional)</Label>
              <Textarea
                value={companyListForm.notes}
                onChange={(e) => setCompanyListForm({ ...companyListForm, notes: e.target.value })}
                placeholder="Any instructions or notes..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCompanyListDialog({ open: false, student: null })}>
              Cancel
            </Button>
            <Button onClick={uploadCompanyList} disabled={!companyListForm.url}>
              <Upload className="h-4 w-4 mr-2" /> Upload & Notify Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
